import { useState, useEffect } from "react";
import {
  verifyEmail,
  verifyResetOtp,
  resendOtp,
} from "../common/services/auth";
import { useAuth } from "../common/store/authStore";

type UseVerifyOtpParams = {
  from: string;
  initialEmail: string;
  showAlert: (type: string, message: string, title?: string | null) => void;
  navigate: any;
};

export default function useVerifyOtp({
  from,
  initialEmail,
  showAlert,
  navigate,
}: UseVerifyOtpParams) {
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState<number>(0);

  const { setAuthenticatedUser } = useAuth();

  useEffect(() => {
    if (cooldown <= 0) return;
    const interval = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) {
          clearInterval(interval);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldown]);

  const handleSubmit = async (
    values: { email: string; otp: string },
    helpers: any,
  ) => {
    setLoading(true);
    if (!values.email) {
      showAlert(
        "error",
        "No email provided. Please go back and re-initiate the flow.",
        "Missing Email",
      );
      helpers.setSubmitting(false);
      setLoading(false);
      return;
    }

    try {
      if (from === "signup") {
        const payload = { email: values.email, otp: values.otp };

        const res = await verifyEmail(payload);
        setAuthenticatedUser(res.user);
        showAlert("success", "Email verified successfully", "Verified");
        setTimeout(() => navigate("/tournaments"), 1000);
      } else {
        const payload = { email: values.email, otp: values.otp };
        await verifyResetOtp(payload);
        showAlert(
          "success",
          "OTP verified. Proceed to reset your password.",
          "Verified",
        );
        setTimeout(
          () => navigate("/reset-password", { state: { email: values.email } }),
          800,
        );
      }
    } catch (error: any) {
      showAlert(
        "error",
        error?.message || "Verification failed",
        "Verification Failed",
      );
    } finally {
      setLoading(false);
      helpers.setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!initialEmail) {
      showAlert(
        "error",
        "No email to resend OTP for. Please go back and re-initiate the flow.",
        "Missing Email",
      );
      return;
    }

    if (cooldown > 0) return;
    setLoading(true);
    try {
      const response = await resendOtp(initialEmail);
      showAlert("success", response.message || "OTP resent", "OTP Sent");
      setCooldown(60);
    } catch (error: any) {
      showAlert(
        "error",
        error?.message || "Could not resend OTP",
        "Resend Failed",
      );
    } finally {
      setLoading(false);
    }
  };

  return {
    handleSubmit,
    handleResend,
    loading,
    cooldown,
    setCooldown,
  };
}
